import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  listAdminNoticesApi,
  createAdminNoticeApi,
  updateAdminNoticeApi,
  deleteAdminNoticeApi,
} from "api/admin";
import {
  C,
  Panel,
  SkeletonRows,
  Empty,
  SearchBar,
  Pagination,
  formatDate,
  primaryBtn,
  primaryBtnSm,
  ghostBtn,
  dangerBtnSm,
  inputStyle,
} from "./admin_ui";

export default function AdminNotices() {
  const [params, setParams] = useSearchParams();
  const search = params.get("q") || "";
  const page = parseInt(params.get("page") || "1", 10) || 1;
  const [searchInput, setSearchInput] = useState(search);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);

  const [editing, setEditing] = useState(null); // null = closed, {} = new, {docId,...} = edit
  const [busy, setBusy] = useState(false);

  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      const r = await listAdminNoticesApi({
        search: search || undefined,
        page,
        limit: 20,
      });
      if (cancelled) return;
      if (r?.success) setData({ items: r.items || [], total: r.total || 0 });
      else setError(r?.error || "조회 실패");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [search, page, tick]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (key !== "page") next.set("page", "1");
    setParams(next, { replace: false });
  };

  const handleSave = async () => {
    if (!editing.title?.trim()) {
      alert("제목을 입력하세요.");
      return;
    }
    setBusy(true);
    const payload = {
      title: editing.title.trim(),
      body: editing.body || "",
      type: editing.type || "공지",
    };
    const r = editing.docId
      ? await updateAdminNoticeApi(editing.docId, payload)
      : await createAdminNoticeApi(payload);
    setBusy(false);
    if (r?.success) {
      setEditing(null);
      setTick((t) => t + 1);
    } else {
      alert(r?.error || "저장 실패");
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("이 공지를 삭제하시겠습니까?")) return;
    const r = await deleteAdminNoticeApi(docId);
    if (r?.success) setTick((t) => t + 1);
    else alert(r?.error || "삭제 실패");
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: C.ink,
              letterSpacing: "-0.02em",
            }}
          >
            공지사항 관리
          </div>
          <div style={{ marginTop: 4, fontSize: 13, color: C.sub }}>
            전체 {data.total.toLocaleString()}건
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            setEditing({ title: "", body: "", type: "공지" })
          }
          style={primaryBtn}
        >
          + 공지 등록
        </button>
      </div>

      {editing && (
        <div
          style={{
            background: "#fff",
            border: `1px solid ${C.line}`,
            borderRadius: 14,
            padding: "18px 22px",
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: C.ink,
              marginBottom: 4,
            }}
          >
            {editing.docId ? "공지 수정" : "공지 등록"}
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <input
              value={editing.type || ""}
              onChange={(e) =>
                setEditing({ ...editing, type: e.target.value })
              }
              placeholder="분류 (예: 공지)"
              style={{ ...inputStyle, width: 200 }}
            />
            <input
              value={editing.title || ""}
              onChange={(e) =>
                setEditing({ ...editing, title: e.target.value })
              }
              placeholder="제목"
              style={{ ...inputStyle, flex: 1, minWidth: 200 }}
            />
          </div>
          <textarea
            value={editing.body || ""}
            onChange={(e) => setEditing({ ...editing, body: e.target.value })}
            rows={8}
            placeholder="본문"
            style={{
              width: "100%",
              padding: "12px 14px",
              borderRadius: 10,
              border: `1px solid ${C.line}`,
              fontSize: 14,
              color: C.ink,
              background: "#fff",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
              lineHeight: 1.6,
            }}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button
              type="button"
              onClick={() => setEditing(null)}
              disabled={busy}
              style={ghostBtn}
            >
              취소
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy}
              style={primaryBtnSm}
            >
              저장
            </button>
          </div>
        </div>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1 }} />
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onSubmit={() => setParam("q", searchInput.trim())}
          placeholder="제목 / 본문 / 분류"
        />
      </div>

      <Panel title="공지 목록" count={data.total}>
        {loading ? (
          <SkeletonRows count={6} />
        ) : error ? (
          <Empty label={error} />
        ) : data.items.length === 0 ? (
          <Empty />
        ) : (
          data.items.map((n) => (
            <div
              key={n.docId}
              style={{
                display: "flex",
                gap: 10,
                padding: "12px 0",
                borderBottom: `1px solid ${C.line}`,
                fontSize: 13,
                alignItems: "flex-start",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: C.greenDark,
                    background: C.greenBg,
                    padding: "2px 8px",
                    borderRadius: 6,
                    display: "inline-block",
                    marginBottom: 6,
                  }}
                >
                  {n.type || "공지"}
                </div>
                <div
                  style={{
                    fontWeight: 700,
                    color: C.ink,
                    marginBottom: 4,
                  }}
                >
                  {n.title || "—"}
                </div>
                <div
                  style={{
                    color: C.body,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontSize: 12,
                  }}
                >
                  {n.body || "—"}
                </div>
                <div
                  style={{ marginTop: 6, fontSize: 11, color: C.sub }}
                >
                  {formatDate(n.createDate)}
                </div>
              </div>
              <div
                style={{ display: "flex", gap: 6, flexShrink: 0 }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setEditing({
                      docId: n.docId,
                      title: n.title || "",
                      body: n.body || "",
                      type: n.type || "공지",
                    })
                  }
                  style={{ ...primaryBtnSm, height: 30, fontSize: 11 }}
                >
                  수정
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(n.docId)}
                  style={{ ...dangerBtnSm, height: 30, fontSize: 11 }}
                >
                  삭제
                </button>
              </div>
            </div>
          ))
        )}
        <Pagination
          page={page}
          limit={20}
          total={data.total}
          onChange={(p) => setParam("page", String(p))}
        />
      </Panel>
    </>
  );
}
